import { createActorContext } from "@xstate/react";
import { Editor } from "tldraw";
import { createMachine, assign, fromCallback } from "xstate";

import {
  annotateFeed,
  clearAuditLog,
} from "@/components/canvas-editor/annotate-feed";
import { replaceCustomNodeParameters } from "@/components/canvas-editor/build-manifest";
import { Audit, AuditLog } from "@/components/canvas-editor/shapes/audit";
import { FeedShape } from "@/components/canvas-editor/shapes/shapes";
import { feedId } from "@/components/canvas-editor/utils";
import { DeliveryStatus, FeedVersion, Manifest, Post } from "@/lib/types";

export function setDebug(
  editor: Editor,
  log: AuditLog | undefined,
  // Called with the number of canvas nodes that had no counterpart in the audit
  // tree, so a caller can say so. Silence is what made the last two colouring
  // bugs survive; a partially coloured canvas should not look complete.
  onUnpaired?: (count: number) => void,
) {
  clearAuditLog(editor);

  const feed = editor.getShape<FeedShape>(feedId);
  if (!feed) return;

  if (log) {
    requestAnimationFrame(() => {
      const unpaired = annotateFeed(editor, feed, log);

      if (unpaired > 0) {
        onUnpaired?.(unpaired);
      }
    });
  }
}

function setAuditLogResultsToIndex(audit: Audit, index: number): Audit {
  const newAudit = {
    ...audit,
    result: [audit.result[index]] as [boolean],
    scores: [audit.scores[index]] as [number],
    // `undecided` holds POST INDICES, so it has to be re-based like the arrays beside it
    // rather than carried over by the spread. Left alone, a node that could not decide post 5
    // arrives at every single-post view still saying `[5]` — which reads as "post 0 is fine"
    // everywhere, and as "post 0 is undecided" on the one view that happens to be post 5.
    undecided: audit.undecided?.includes(index) ? [0] : undefined,
  };

  if (audit.sub_audit) {
    audit.sub_audit = [...audit.sub_audit];
  }

  if (newAudit.sub_audit) {
    for (let i = 0; i < newAudit.sub_audit.length; i++) {
      const subAudit = newAudit.sub_audit[i];

      if (subAudit) {
        newAudit.sub_audit[i] = setAuditLogResultsToIndex(subAudit, index);
      }
    }
  }

  return newAudit;
}

function setResultsToIndex(audit: AuditLog, index: number) {
  const newAudit = {
    ...audit,
    results: [audit.results[index]] as [boolean],
  };
  newAudit.audit = setAuditLogResultsToIndex(newAudit.audit, index);
  return newAudit;
}

interface AuditResults {
  [x: string]: AuditLog | undefined;
}

export type Panel = "feed" | "generate-feed" | "post" | "add-to-feed";

interface CanvasEditorContext {
  visiblePanel: Panel | undefined;
  debugMessage: string;
  debugResults: AuditResults;
  debugPostUrl: string | undefined;
  pendingAuditResults: string[];
  debugHiddenStatuses: Record<string, boolean>;
  debugPresenceStatuses: Record<string, 0 | 1>;
  debugDeliveryStatuses: Record<string, DeliveryStatus>;
  generateMessage: string;
  generatedFeed: Post[];
  debugError: boolean;
}

interface SetupEvent {
  type: "SETUP";
  input: {
    visiblePanel: Panel | undefined;
    debugResults: Record<string, AuditLog | undefined>;
  };
}

interface SetVisiblePanelEvent {
  type: "SET_VISIBLE_PANEL";
  panel: Panel | undefined;
}

interface SetDebugMessageEvent {
  type: "SET_DEBUG_MESSAGE";
  message: string;
}

interface SetDebugErrorEvent {
  type: "SET_DEBUG_ERROR";
}

interface SetDebugResultsEvent {
  type: "SET_DEBUG_RESULTS";
  results: AuditResults;
  debugPostUrl: string | undefined;
}

interface SetDebugResultUrlEvent {
  type: "SET_DEBUG_RESULT_URL";
  url?: string;
}

interface SetGenerateMessageEvent {
  type: "SET_GENERATE_MESSAGE";
  message: string;
}

interface AddToFeedEvent {
  type: "ADD_TO_FEED";
  feed: Post[];
}

interface CompleteAuditEvent {
  type: "COMPLETE_AUDIT";
}

interface CompleteGenerateEvent {
  type: "COMPLETE_GENERATE";
}

interface StartAuditEvent {
  type: "START_AUDIT";
  urls: string[];
  manifest: Manifest;
  editor: Editor;
  algo_id: number | undefined;
}

interface RemoveAuditItemEvent {
  type: "REMOVE_AUDIT_ITEM";
  url: string;
}

interface GenerateFeedEvent {
  type: "GENERATE_FEED";
  feedVersion: FeedVersion;
}

interface RegenerateFeedEvent {
  type: "REGENERATE_FEED";
  feedVersion: FeedVersion;
}

interface SetDebugHiddenStatusesEvent {
  type: "SET_DEBUG_HIDDEN_STATUSES";
  hiddenStatuses: Array<{ uri: string; hidden: boolean }>;
}

interface SetDebugPresenceStatusesEvent {
  type: "SET_DEBUG_PRESENCE_STATUSES";
  presenceStatuses: Array<{ uri: string; present: 0 | 1 }>;
}

interface SetDebugDeliveryStatusesEvent {
  type: "SET_DEBUG_DELIVERY_STATUSES";
  deliveryStatuses: DeliveryStatus[];
}

type CanvasEditorEvent =
  | SetVisiblePanelEvent
  | SetupEvent
  | StartAuditEvent
  | SetDebugMessageEvent
  | SetDebugResultsEvent
  | SetDebugResultUrlEvent
  | CompleteAuditEvent
  | GenerateFeedEvent
  | SetGenerateMessageEvent
  | AddToFeedEvent
  | CompleteGenerateEvent
  | RegenerateFeedEvent
  | SetDebugErrorEvent
  | SetDebugHiddenStatusesEvent
  | SetDebugPresenceStatusesEvent
  | SetDebugDeliveryStatusesEvent
  | RemoveAuditItemEvent;

export const canvasEditorMachine = createMachine(
  {
    id: "canvasEditor",
    initial: "idle",
    types: {
      context: {} as CanvasEditorContext,
      events: {} as CanvasEditorEvent,
    },
    context: {
      visiblePanel: undefined,
      debugError: false,
      debugMessage: "",
      debugResults: {},
      debugPostUrl: undefined,
      generateMessage: "",
      generatedFeed: [],
      debugHiddenStatuses: {},
      debugPresenceStatuses: {},
      debugDeliveryStatuses: {},
      pendingAuditResults: [],
    },
    entry: assign({
      visiblePanel: ({ event }) => {
        if ("input" in event === false) {
          return undefined;
        }

        return event.input.visiblePanel;
      },
      debugResults: ({ event }) => {
        if ("input" in event === false) {
          return {};
        }

        return event.input.debugResults || {};
      },
    }),
    on: {
      SET_DEBUG_RESULT_URL: { actions: "setDebugResultUrl" },
      SET_VISIBLE_PANEL: { actions: "setVisiblePanel" },
      SET_DEBUG_MESSAGE: { actions: "setDebugMessage" },
      SET_DEBUG_ERROR: { actions: "setDebugError" },
      SET_DEBUG_RESULTS: { actions: "setDebugResults" },
      SET_GENERATE_MESSAGE: { actions: "setGenerateMessage" },
      SET_DEBUG_HIDDEN_STATUSES: { actions: "setDebugHiddenStatuses" },
      SET_DEBUG_PRESENCE_STATUSES: { actions: "setDebugPresenceStatuses" },
      SET_DEBUG_DELIVERY_STATUSES: { actions: "setDebugDeliveryStatuses" },
      ADD_TO_FEED: { actions: "addToFeed" },
      REMOVE_AUDIT_ITEM: {
        actions: ["removeAuditItem", "onRemoveAuditItem"],
      },
    },
    type: "parallel",
    states: {
      idle: {
        type: "parallel",
        states: {
          debugPost: {
            initial: "idle",
            states: {
              idle: {
                on: {
                  START_AUDIT: { target: "running", guard: "isFormValid" },
                },
              },
              running: {
                entry: [
                  "setPostPanelVisible",
                  "resetDebugError",
                  "setPendingAuditResults",
                  "onDebugPost",
                ],
                invoke: {
                  id: "audit",
                  input: ({ event }) => {
                    if (event.type !== "START_AUDIT") {
                      throw new Error("Invalid event type");
                    }

                    return {
                      urls: event.urls,
                      manifest: event.manifest,
                      editor: event.editor,
                      algo_id: event.algo_id,
                    };
                  },
                  src: fromCallback(({ input: i, sendBack }) => {
                    const input = i as {
                      urls: string[];
                      manifest: Manifest;
                      editor: Editor;
                      algo_id: number | undefined;
                    };

                    setDebug(input.editor, undefined);
                    sendBack({
                      type: "SET_DEBUG_MESSAGE",
                      message: "Starting audit...",
                    });

                    const startAuditObj: AuditResults = {};

                    for (const url of input.urls) {
                      startAuditObj[url] = undefined;
                    }

                    sendBack({
                      type: "SET_DEBUG_RESULTS",
                      results: startAuditObj,
                    });

                    const ws = new WebSocket(
                      `wss://api.graze.social/app/api/v1/workbench/live-audit`,
                    );

                    ws.onopen = () => {
                      ws.send(
                        JSON.stringify({
                          algo_id: input.algo_id,
                          urls: input.urls,
                          manifest: input.manifest.filter.metadata
                            ?.customNodeParameters
                            ? {
                                ...input.manifest,
                                filter: replaceCustomNodeParameters(
                                  input.manifest.filter,
                                  input.manifest.filter.metadata
                                    ?.customNodeParameters,
                                ),
                              }
                            : input.manifest,
                        }),
                      );
                    };

                    // Only an error, an empty result set or an audit_log takes this
                    // actor out of `running` -- "Task completed." is deliberately
                    // ignored below so it cannot overwrite the last real message. So if
                    // the socket ever ends without one of those, the canvas spins
                    // forever with no way back. That is not hypothetical: it is exactly
                    // what a feed whose manifest the server could not evaluate looked
                    // like to the user who reported this. The server should always send
                    // something, and now does, but the client should not depend on that
                    // to stop spinning.
                    let settled = false;
                    const settle = (message?: string) => {
                      if (settled) return;
                      settled = true;
                      if (message) {
                        sendBack({ type: "SET_DEBUG_MESSAGE", message });
                        sendBack({ type: "SET_DEBUG_ERROR" });
                      }
                      sendBack({ type: "COMPLETE_AUDIT" });
                    };

                    ws.onmessage = (socketEvent) => {
                      try {
                        const json = JSON.parse(socketEvent.data as string) as (
                          | { status: string }
                          | { error: string }
                          | { audit_log: AuditLog }
                        ) & {
                          hidden_statuses?: Array<{
                            uri: string;
                            hidden: boolean;
                          }>;
                          presence_statuses?: Array<{
                            uri: string;
                            present: 0 | 1;
                          }>;
                          delivery_statuses?: DeliveryStatus[];
                        };

                        if (
                          json.hidden_statuses &&
                          json.hidden_statuses.length > 0
                        ) {
                          sendBack({
                            type: "SET_DEBUG_HIDDEN_STATUSES",
                            hiddenStatuses: json.hidden_statuses,
                          });
                        }

                        if (
                          json.presence_statuses &&
                          json.presence_statuses.length > 0
                        ) {
                          sendBack({
                            type: "SET_DEBUG_PRESENCE_STATUSES",
                            presenceStatuses: json.presence_statuses,
                          });
                        }

                        // Absent on a self-emitting feed, which is almost all of
                        // them -- the server sends nothing rather than a list of
                        // "delivered as itself", so there is nothing to clear.
                        if (
                          json.delivery_statuses &&
                          json.delivery_statuses.length > 0
                        ) {
                          sendBack({
                            type: "SET_DEBUG_DELIVERY_STATUSES",
                            deliveryStatuses: json.delivery_statuses,
                          });
                        }

                        if ("status" in json) {
                          if (json.status !== "Task completed.") {
                            sendBack({
                              type: "SET_DEBUG_MESSAGE",
                              message: json.status,
                            });
                          }
                        } else if ("error" in json) {
                          sendBack({
                            type: "SET_DEBUG_MESSAGE",
                            message: json.error,
                          });
                          sendBack({ type: "SET_DEBUG_ERROR" });
                          ws.close();
                          settle();
                        } else if (json.audit_log.results.length === 0) {
                          sendBack({
                            type: "SET_DEBUG_MESSAGE",
                            message: "No results.",
                          });
                          ws.close();
                          settle();
                        } else if (json.audit_log) {
                          sendBack({ type: "SET_DEBUG_MESSAGE", message: "" });

                          const newData: AuditResults = {};
                          let lastData;
                          let debugPostUrl: string | undefined;

                          for (let i = 0; i < input.urls.length; i++) {
                            debugPostUrl = input.urls[i];

                            if (debugPostUrl) {
                              newData[debugPostUrl] = setResultsToIndex(
                                json.audit_log,
                                i,
                              );
                              lastData = newData[debugPostUrl];
                            }
                          }

                          ws.close();
                          setDebug(input.editor, lastData, (unpaired) => {
                            sendBack({
                              type: "SET_DEBUG_MESSAGE",
                              message:
                                `${unpaired} node${unpaired === 1 ? "" : "s"} on this canvas ` +
                                `could not be matched to the result and ${unpaired === 1 ? "was" : "were"} ` +
                                "left uncoloured -- the canvas has drifted from the saved rules. " +
                                "The JSON view shows the full result.",
                            });
                          });
                          sendBack({
                            type: "SET_DEBUG_RESULTS",
                            results: newData,
                            debugPostUrl,
                          });
                          settle();
                        }
                      } catch (e) {
                        console.error(e);
                      }
                    };

                    ws.onerror = () => {
                      settle("Lost connection to the debugger. Please try again.");
                    };

                    ws.onclose = () => {
                      // A close after a result is the normal path: `settled` is already
                      // true, so this is a no-op and the message stays whatever the
                      // result set it to.
                      settle("The debugger closed before returning a result.");
                    };

                    return () => {
                      settled = true;
                      ws.close();
                    };
                  }),
                },
                exit: ["clearPendingAuditResults"],
                on: {
                  COMPLETE_AUDIT: { target: "idle" },
                  START_AUDIT: { target: "running", reenter: true },
                },
              },
            },
          },
          generateFeed: {
            initial: "idle",
            states: {
              idle: {
                on: {
                  GENERATE_FEED: [
                    { target: "running", guard: "isFormValid" },
                    { target: "invalid" },
                  ],
                },
              },
              invalid: {
                entry: "reportValidity",
                on: {
                  GENERATE_FEED: { target: "running", guard: "isFormValid" },
                },
              },
              running: {
                invoke: {
                  id: "feed",
                  input: ({ event }) => {
                    if (
                      event.type !== "GENERATE_FEED" &&
                      event.type !== "REGENERATE_FEED"
                    ) {
                      throw new Error("Invalid event type");
                    }

                    return {
                      feedVersion: event.feedVersion,
                    };
                  },
                  src: fromCallback(({ input: i, sendBack }) => {
                    const input = i as { feedVersion: FeedVersion };
                    const ws = new WebSocket(
                      `wss://api.graze.social/app/api/v1/workbench/live-query`,
                    );

                    ws.onopen = () => {
                      ws.send(
                        JSON.stringify(
                          input.feedVersion.manifest.filter.metadata
                            ?.customNodeParameters
                            ? {
                                ...input.feedVersion,
                                manifest: {
                                  ...input.feedVersion.manifest,
                                  filter: replaceCustomNodeParameters(
                                    input.feedVersion.manifest.filter,
                                    input.feedVersion.manifest.filter.metadata
                                      ?.customNodeParameters,
                                  ),
                                },
                              }
                            : input.feedVersion,
                        ),
                      );
                    };

                    ws.onmessage = (event) => {
                      try {
                        const json = JSON.parse(event.data as string) as
                          | { status: string }
                          | { record_count: number; posts: Post[] };

                        if ("status" in json) {
                          sendBack({
                            type: "SET_GENERATE_MESSAGE",
                            message: json.status,
                          });
                        } else {
                          sendBack({
                            type: "SET_GENERATE_MESSAGE",
                            message: `Scanned ${json.record_count} posts...`,
                          });
                          sendBack({
                            type: "ADD_TO_FEED",
                            feed: json.posts,
                          });
                        }
                      } catch (e) {
                        console.error(e);
                      }
                    };

                    ws.onclose = () => {
                      sendBack({ type: "COMPLETE_GENERATE" });
                    };

                    return () => {
                      ws.close();
                    };
                  }),
                },
                on: {
                  COMPLETE_GENERATE: { target: "idle" },
                  REGENERATE_FEED: [
                    {
                      target: "running",
                      reenter: true,
                      guard: "isFormValid",
                      actions: ["clearGeneratedFeed"],
                    },
                    { target: "invalid" },
                  ],
                },
              },
            },
          },
        },
      },
    },
  },
  {
    guards: {
      isFormValid: () => {
        const form = document.querySelector<HTMLFormElement>("form");

        if (!form) {
          return true;
        }

        const isValid = form.checkValidity();

        return isValid;
      },
    },
    actions: {
      setDebugResultUrl: assign({
        debugPostUrl: ({ event }) => {
          if (event.type !== "SET_DEBUG_RESULT_URL") {
            throw new Error("Invalid event type");
          }

          return event.url;
        },
      }),
      removeAuditItem: assign({
        debugResults: ({ context, event }) => {
          if (event.type !== "REMOVE_AUDIT_ITEM") {
            throw new Error("Invalid event type");
          }

          const newDebugResults = { ...context.debugResults };
          delete newDebugResults[event.url];

          return newDebugResults || {};
        },
      }),
      setPendingAuditResults: assign({
        pendingAuditResults: ({ event }) => {
          if (event.type !== "START_AUDIT") {
            throw new Error("Invalid event type");
          }

          return event.urls;
        },
      }),
      clearPendingAuditResults: assign({
        pendingAuditResults: [],
      }),
      setDebugHiddenStatuses: assign({
        debugHiddenStatuses: ({ event, context }) => {
          if (event.type !== "SET_DEBUG_HIDDEN_STATUSES") {
            throw new Error("Invalid event type");
          }

          const newStatuses = { ...context.debugHiddenStatuses };

          for (const status of event.hiddenStatuses) {
            newStatuses[status.uri] = status.hidden;
          }

          return newStatuses;
        },
      }),
      setDebugPresenceStatuses: assign({
        debugPresenceStatuses: ({ event, context }) => {
          if (event.type !== "SET_DEBUG_PRESENCE_STATUSES") {
            throw new Error("Invalid event type");
          }

          const newStatuses = { ...context.debugPresenceStatuses };

          for (const status of event.presenceStatuses) {
            newStatuses[status.uri] = status.present;
          }

          return newStatuses;
        },
      }),
      setDebugDeliveryStatuses: assign({
        debugDeliveryStatuses: ({ event, context }) => {
          if (event.type !== "SET_DEBUG_DELIVERY_STATUSES") {
            throw new Error("Invalid event type");
          }

          const newStatuses = { ...context.debugDeliveryStatuses };

          for (const status of event.deliveryStatuses) {
            newStatuses[status.uri] = status;
          }

          return newStatuses;
        },
      }),
      resetDebugError: assign({
        debugError: false,
      }),
      setDebugError: assign({
        debugError: true,
      }),
      reportValidity: () => {
        const form = document.querySelector<HTMLFormElement>("form");

        if (!form) {
          return false;
        }

        form.reportValidity();
      },
      clearGeneratedFeed: assign({
        generatedFeed: [],
      }),
      setVisiblePanel: assign({
        visiblePanel: ({ event }) => {
          if (event.type !== "SET_VISIBLE_PANEL") {
            throw new Error("Invalid event type");
          }

          return event.panel;
        },
      }),
      setDebugMessage: assign({
        debugMessage: ({ event }) => {
          if (event.type !== "SET_DEBUG_MESSAGE") {
            throw new Error("Invalid event type");
          }

          return event.message;
        },
      }),
      setDebugResults: assign({
        debugPostUrl: ({ event }) => {
          if (event.type !== "SET_DEBUG_RESULTS") {
            throw new Error("Invalid event type");
          }

          return event.debugPostUrl;
        },
        debugResults: ({ context, event }) => {
          if (event.type !== "SET_DEBUG_RESULTS") {
            throw new Error("Invalid event type");
          }

          return {
            ...context.debugResults,
            ...event.results,
          };
        },
      }),
      setGenerateMessage: assign({
        generateMessage: ({ event }) => {
          if (event.type !== "SET_GENERATE_MESSAGE") {
            throw new Error("Invalid event type");
          }

          return event.message;
        },
      }),
      addToFeed: assign({
        generatedFeed: ({ context, event }) => {
          if (event.type !== "ADD_TO_FEED") {
            throw new Error("Invalid event type");
          }

          return [...context.generatedFeed, ...event.feed];
        },
      }),
      setPostPanelVisible: assign({
        visiblePanel: "post",
      }),
    },
  },
);

export const CanvasEditorMachine = createActorContext(canvasEditorMachine);
